import React, { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../../api";
import type { ForecastResponse } from "../../types";
import { useToast } from "../../context/ToastContext";
import { formatCurrency } from "../../lib/format";
import AutoDashboard from "./AutoDashboard";
import PeriodControls, { defaultPeriod, periodQuery, type PeriodState } from "./PeriodControls";

// §9 + tests #18-20: pipeline forecast = amount * stage probability, live
// from open opportunities. Probabilities are stage-configurable
// (director/admin only, PATCH /forecast/probabilities/:stage_code) and
// snapshots are append-only/idempotent (POST /forecast/snapshot) — this
// panel never recomputes weighted_amount itself, it only displays what the
// backend returned and lets an authorized user trigger those two writes.
export default function ForecastPanel({ canEditProbabilities }: { canEditProbabilities: boolean }) {
  const { show } = useToast();
  const [period, setPeriod] = useState<PeriodState>(defaultPeriod());
  const [data, setData] = useState<ForecastResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stageEdits, setStageEdits] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .getForecast(periodQuery(period))
      .then(setData)
      .catch((e) => setError(e instanceof ApiClientError ? e.message : "No se pudo cargar el forecast."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.from, period.to]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.rows || data?.opportunities || [];
  const stageProbabilities = React.useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r: any) => {
      if (r.stage_code && r.probability !== undefined) m.set(r.stage_code, r.probability);
    });
    return m;
  }, [rows]);

  async function saveProbability(stageCode: string) {
    const raw = stageEdits[stageCode];
    if (raw === undefined || raw === "") return;
    const n = Number(raw);
    if (Number.isNaN(n)) {
      show("Probabilidad inválida.", "error");
      return;
    }
    setBusy(true);
    try {
      await api.setForecastProbability(stageCode, n > 1 ? n / 100 : n);
      show("Probabilidad actualizada.", "success");
      setStageEdits((m) => ({ ...m, [stageCode]: "" }));
      load();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo actualizar.", "error");
    } finally {
      setBusy(false);
    }
  }

  async function snapshot() {
    setBusy(true);
    try {
      await api.snapshotForecast();
      show("Snapshot del forecast de hoy guardado.", "success");
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo guardar el snapshot.", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <PeriodControls period={period} onChange={setPeriod} allowCompare={false} />
        {canEditProbabilities && (
          <button className="btn-ghost" disabled={busy} onClick={snapshot}>
            Guardar snapshot de hoy
          </button>
        )}
      </div>

      {loading && <div className="loading-strip">Cargando forecast...</div>}
      {error && <p className="form-error">{error}</p>}

      {!loading && data && (
        <>
          <div className="kpi-cards">
            <div className="kpi-card">
              <div className="muted">Pipeline bruto</div>
              <div className="big">{formatCurrency(data.pipeline_bruto ?? rows.reduce((s: number, r: any) => s + (r.amount || r.gross_amount || 0), 0))}</div>
            </div>
            <div className="kpi-card">
              <div className="muted">Pipeline ponderado</div>
              <div className="big">
                {formatCurrency(data.pipeline_ponderado ?? data.weighted_pipeline ?? rows.reduce((s: number, r: any) => s + (r.weighted_amount || 0), 0))}
              </div>
            </div>
          </div>

          {canEditProbabilities && stageProbabilities.size > 0 && (
            <div className="section-card">
              <h2>Probabilidad por etapa</h2>
              <table>
                <thead>
                  <tr>
                    <th>Etapa</th>
                    <th>Probabilidad actual</th>
                    <th>Nueva (%)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(stageProbabilities.entries()).map(([stage, prob]) => (
                    <tr key={stage}>
                      <td>{stage}</td>
                      <td>{(prob * 100).toFixed(0)}%</td>
                      <td>
                        <input
                          style={{ width: 70 }}
                          placeholder={(prob * 100).toFixed(0)}
                          value={stageEdits[stage] || ""}
                          onChange={(e: any) => setStageEdits((m) => ({ ...m, [stage]: e.target.value }))}
                        />
                      </td>
                      <td>
                        <button className="link-btn" disabled={busy} onClick={() => saveProbability(stage)}>
                          Guardar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="section-card">
            <h2>Pipeline por oportunidad</h2>
            <AutoDashboard data={{ rows }} />
          </div>
        </>
      )}
    </div>
  );
}
